import { Icon, IconButton, Box } from '@chakra-ui/react';
import { CirclePlus, SquarePen } from 'lucide-react';
import React, { useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';

import { DISABLE_CREATE_CASE } from '@app/constants/permissions';
import { useCheckPermissions } from '@app/helpers/common';
import { Alert } from '@chakra-snippets/alert';
import { Button } from '@chakra-snippets/button';

import CreateCaseModal from './create-case-modal';
import useScreenSize from '../Hooks/use-screen-size';

interface CreateCaseButtonProps {
  buttonText?: string;
  size?: 'sm' | 'md' | 'lg';
}

const CreateCaseButton = ({ buttonText, size = 'md' }: CreateCaseButtonProps) => {
  const history = useNavigate();
  const location = useLocation();
  const isSmallScreen = useScreenSize();
  const isNavigatingRef = useRef(false);
  const isCreateCaseDisabled = useCheckPermissions([DISABLE_CREATE_CASE]);

  const handleCreateClick = useCallback(() => {
    if (isNavigatingRef.current) return;
    isNavigatingRef.current = true;

    const currentPath = location.pathname;
    const queryParams = new URLSearchParams(location.search);

    const phone = queryParams.get('phone') || queryParams.get('search');
    if (!currentPath.endsWith('/create')) {
      history(`${currentPath}/create${phone ? `?phone=${phone}` : ''}`);
    }

    setTimeout(() => {
      isNavigatingRef.current = false;
    }, 100);
  }, [location.pathname, history, location.search]);

  const shouldShowModal = useMemo(() => {
    const path = location.pathname;
    return path.includes('create') || path.includes('edit');
  }, [location.pathname]);

  if (isSmallScreen) {
    if (isCreateCaseDisabled) {
      return (
        <Box />
      );
    }
    return (
      <>
        <IconButton
          data-testid="create-case-button"
          size={size}
          onClick={handleCreateClick}
          variant="ghost"
        >
          <SquarePen />
        </IconButton>
        {shouldShowModal && <CreateCaseModal />}
      </>
    );
  }

  if (isCreateCaseDisabled) {
    return (
      <Alert
        title="Creating a case is unavailable for your organization. Please go to your claims management system to create a new case."
        status="info"
        w="50%"
      />
    );
  }

  return (
    <>
      <Button
        data-testid="create-case-button"
        size={size}
        onClick={handleCreateClick}
      >
        <Icon>
          <CirclePlus />
        </Icon>
        {buttonText || 'Create Case'}
      </Button>
      {shouldShowModal && <CreateCaseModal />}
    </>
  );
};

export default CreateCaseButton;
